import type { ReactNode } from 'react';
import { CheckCircle, CircleNotch, WarningCircle } from '@phosphor-icons/react';

export function Card({ children, className = '', interactive = false }: { children: ReactNode; className?: string; interactive?: boolean }) {
  return <section className={`card ${interactive ? 'card--interactive' : ''} ${className}`}>{children}</section>;
}

export function PageHeader({ eyebrow, title, description, actions }: {
  eyebrow?: string; title: string; description?: string; actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow && <span className="page-header__eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}

export function StatusBadge({ status, label }: { status: string; label: string }) {
  const tone = /succeeded|approved|active|ready|resolved/.test(status)
    ? 'success' : /failed|critical|rejected/.test(status) ? 'danger' : /running|review|medium|queued/.test(status) ? 'warning' : 'neutral';
  return <span className={`status status--${tone}`}><i />{label}</span>;
}

export function EmptyState({ icon, title, description, action }: {
  icon?: ReactNode; title: string; description: string; action?: ReactNode;
}) {
  return <div className="empty-state"><div className="empty-state__icon">{icon}</div><h3>{title}</h3><p>{description}</p>{action}</div>;
}

export function LoadingState({ label = 'در حال دریافت اطلاعات' }: { label?: string }) {
  return <div className="loading-state"><CircleNotch size={24} className="spin" /><span>{label}</span></div>;
}

export function InlineNotice({ tone = 'info', children }: { tone?: 'info' | 'success' | 'warning' | 'danger'; children: ReactNode }) {
  const Icon = tone === 'success' ? CheckCircle : tone === 'warning' || tone === 'danger' ? WarningCircle : CheckCircle;
  return <div className={`notice notice--${tone}`}><Icon size={20} weight="fill" /><div>{children}</div></div>;
}

/**
 * The sweep is drawn from `--progress`. The previous ring painted two fixed
 * border colours, so it looked identical at 5% and at 100% — a control that
 * claims to show a value and does not.
 */
export function ProgressRing({ value, label }: { value: number; label: string }) {
  const normalized = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));
  return (
    <div
      className="progress-ring"
      style={{ '--progress': `${normalized * 3.6}deg` } as React.CSSProperties}
      role="img"
      aria-label={`${label}: ${normalized.toLocaleString('fa-IR', { maximumFractionDigits: 0 })}٪`}
    >
      <div><strong>{normalized.toLocaleString('fa-IR', { maximumFractionDigits: 0 })}</strong><small>{label}</small></div>
    </div>
  );
}
